import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from '../constants';
import { Product } from '../types';

interface ProductsState {
  items: Product[];
  loading: boolean;
  error: string | null;
  selectedCategoryId: string | null;
  selectedProduct: Product | null;
  currentPage: number;
  totalPages: number;
  totalProducts: number;
  pageSize: number;
}

const initialState: ProductsState = {
  items: [],
  loading: false,
  error: null,
  selectedCategoryId: null,
  selectedProduct: null,
  currentPage: 1,
  totalPages: 1,
  totalProducts: 0,
  pageSize: 10
};

interface FetchProductsResponse {
  products: Product[];
  currentPage: number;
  totalPages: number;
  totalProducts: number;
}

export const fetchProducts = createAsyncThunk<
  FetchProductsResponse,
  {
    categoryId?: string;
    maxPrice?: number;
    color?: string;
    q?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    minRating?: number;
  } | void
>(
  'products/fetchAll',
  async (arg) => {
    const params: Record<string, string> = {};
    const sortBy = arg && 'sortBy' in arg ? arg.sortBy : undefined;
    const minRating = arg && 'minRating' in arg ? arg.minRating : undefined;
    if (typeof sortBy === 'string' && sortBy && sortBy !== 'newest') {
      params.sortBy = sortBy;
    }
    if (typeof minRating === 'number' && minRating > 0) {
      params.minRating = String(minRating);
    }
    const categoryId = arg && 'categoryId' in arg ? arg.categoryId : undefined;
    const maxPrice = arg && 'maxPrice' in arg ? arg.maxPrice : undefined;
    const color = arg && 'color' in arg ? arg.color : undefined;
    const q = arg && 'q' in arg ? arg.q : undefined;
    const page = arg && 'page' in arg ? arg.page : undefined;
    const limit = arg && 'limit' in arg ? arg.limit : undefined;
    if (categoryId) {
      params.category = categoryId;
    }
    if (typeof maxPrice === 'number') {
      params.maxPrice = String(maxPrice);
    }
    if (typeof color === 'string' && color.trim()) {
      params.color = color.trim();
    }
    if (typeof q === 'string' && q.trim()) {
      params.q = q.trim();
    }
    if (typeof page === 'number') {
      params.page = String(page);
    }
    if (typeof limit === 'number') {
      params.limit = String(limit);
    }

    const response = await apiClient.get(`${API_ENDPOINTS.PRODUCTS}`, {
      params
    });
    return response.data as FetchProductsResponse;
  }
);

export const fetchProductById = createAsyncThunk<Product, string>(
  'products/fetchById',
  async (id: string) => {
    const response = await apiClient.get(`${API_ENDPOINTS.PRODUCTS}/${id}`);
    return response.data as Product;
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setSelectedCategory(state, action) {
      state.selectedCategoryId = action.payload;
    },
    clearSelectedProduct(state) {
      state.selectedProduct = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.products;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.totalProducts = action.payload.totalProducts;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load products';
      })
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedProduct = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProduct = action.payload;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load product';
      });
  }
});

export const { setSelectedCategory, clearSelectedProduct } = productsSlice.actions;

export default productsSlice.reducer;

